import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Users, Home, CheckCircle, AlertCircle, DollarSign,
  Heart, ScanLine, XCircle, HeartHandshake,
  Calendar, Navigation, Wallet
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`
});

const RetreatDashboard = () => {
  const [stats, setStats] = useState(null);
  const [finance, setFinance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [attendeesRes, roomsRes, cancellationsRes, donationsRes, refundsRes, financeRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/retreat/attendees`, { headers: getAuthHeaders() }),
          axios.get(`${BACKEND_URL}/api/retreat/rooms`, { headers: getAuthHeaders() }),
          axios.get(`${BACKEND_URL}/api/retreat/cancellations/recent?days=7`, { headers: getAuthHeaders() }).catch(() => ({ data: { count: 0, cancellations: [] } })),
          axios.get(`${BACKEND_URL}/api/retreat/donations`, { headers: getAuthHeaders() }).catch(() => ({ data: { total_donated: 0, donor_count: 0, donations: [] } })),
          axios.get(`${BACKEND_URL}/api/retreat/refunds`, { headers: getAuthHeaders() }).catch(() => ({ data: { total_owed: 0, owed_count: 0, total_paid: 0, refunds: [] } })),
          axios.get(`${BACKEND_URL}/api/retreat/finance/summary`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
        ]);
        setFinance(financeRes.data);

        const attendees = attendeesRes.data;
        const rooms = roomsRes.data;
        const recentCancellations = cancellationsRes.data;
        const donations = donationsRes.data;
        const refunds = refundsRes.data;

        // Revenue
        const totalRevenue = attendees.reduce((sum, a) => sum + (a.amount_paid || 0), 0);
        const totalExpected = attendees.filter(a => a.ticket_status !== 'cancelled' && a.ticket_status !== 'waitlist').length * 325;

        // Scholarships — defined as: attendee has a non-null `scholarship_amount`
        // (set during CSV import when the "Amount Paid" column contained "scholarship")
        const scholarshipRecipients = attendees.filter(a => a.scholarship_amount !== null && a.scholarship_amount !== undefined);
        const scholarshipAmountTotal = scholarshipRecipients.reduce((sum, a) => sum + (a.scholarship_amount || 0), 0);

        // "Total Attendees" = confirmed headcount coming to the event.
        // Excludes cancelled (won't show up) and waitlist (not confirmed).
        const activeAttendees = attendees.filter(
          a => a.ticket_status !== 'cancelled' && a.ticket_status !== 'waitlist'
        );

        setStats({
          totalAttendees: activeAttendees.length,
          paidFull: attendees.filter(a => a.ticket_status === 'paid_full').length,
          depositPaid: attendees.filter(a => a.ticket_status === 'deposit_paid').length,
          pending: attendees.filter(a => a.ticket_status === 'pending').length,
          waitlist: attendees.filter(a => a.ticket_status === 'waitlist').length,
          totalRooms: rooms.length,
          assignedRooms: new Set(attendees.filter(a => a.room_id).map(a => a.room_id)).size,
          unassignedAttendees: attendees.filter(a => !a.room_id).length,
          scholarshipCount: scholarshipRecipients.length,
          scholarshipAmountTotal,
          scholarshipRecipients,
          volunteers: attendees.filter(a => a.willing_to_serve && a.willing_to_serve.toLowerCase().includes('yes')).length,
          totalRevenue,
          totalExpected,
          cancelledCount: attendees.filter(a => a.ticket_status === 'cancelled').length,
          recentCancellations: recentCancellations.cancellations || [],
          recentCancellationCount: recentCancellations.count || 0,
          totalDonated: donations.total_donated || 0,
          donorCount: donations.donor_count || 0,
          totalRefundsOwed: refunds.total_owed || 0,
          refundsOwedCount: refunds.owed_count || 0,
          totalRefundsPaid: refunds.total_paid || 0,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="retreat-dashboard">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Retreat operations at a glance</p>
      </div>

      {/* Attendee comms reference — mirror of the email blast so admins have it handy */}
      <Card className="mb-6 border-0 shadow-sm" data-testid="dashboard-comms-card">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-amber-700" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold tracking-widest uppercase text-amber-700 mb-1">
                  Cancel-by deadline
                </p>
                <p className="text-sm text-gray-800 leading-relaxed">
                  Attendees who can't make the September 18–20 retreat must confirm by{' '}
                  <span className="font-semibold">June 30th</span> so we can finalize headcounts, rooms, and the caravan.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  They mark themselves cancelled in the portal AND email{' '}
                  <a href="mailto:info@rojoretreat.com" className="text-gray-700 underline underline-offset-2">info@rojoretreat.com</a>{' '}
                  to choose donate vs. refund.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center">
                <Navigation className="w-4 h-4 text-orange-700" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold tracking-widest uppercase text-orange-700 mb-1">
                  Hotels &amp; caravan pickup
                </p>
                <p className="text-sm text-gray-800 leading-relaxed">
                  <span className="font-semibold">Primary:</span> DoubleTree · 7800 Normandale Blvd · shuttle/caravan departs here only.
                </p>
                <p className="text-sm text-gray-800 leading-relaxed mt-0.5">
                  <span className="font-semibold">Secondary:</span> DoubleTree Park Place · 1500 Park Place Blvd · attendees must get to Normandale for departure.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Financial summary — the "how short are we" strip */}
      {finance && (
        <Card className="mb-6 border-0 shadow-sm bg-gradient-to-r from-gray-900 to-gray-800 text-white">
          <CardContent className="py-5">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 items-center">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Goal ({finance.headcount} × ${finance.base_price})</p>
                <p className="text-2xl font-bold mt-1" data-testid="finance-goal">${finance.goal.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Scholarships ({finance.scholarship_count})</p>
                <p className="text-2xl font-bold mt-1 text-pink-300" data-testid="finance-scholarships">−${finance.scholarship_total.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Cash Collected</p>
                <p className="text-2xl font-bold mt-1 text-emerald-400" data-testid="finance-collected">${finance.cash_collected.toLocaleString()}</p>
                <p className="text-[11px] text-gray-400">of ${finance.cash_goal.toLocaleString()} cash goal</p>
                {(finance.cancelled_paid > 0 || finance.refunds_paid > 0) && (
                  <p className="text-[11px] text-gray-500" data-testid="finance-cancelled-note">incl. ${(finance.cancelled_paid || 0).toLocaleString()} from cancelled · −${(finance.refunds_paid || 0).toLocaleString()} refunded</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Outstanding Balances</p>
                <p className="text-2xl font-bold mt-1 text-amber-300" data-testid="finance-outstanding">${finance.outstanding_balances.toLocaleString()}</p>
              </div>
              <div className={`rounded-xl px-4 py-3 ${finance.shortfall > 0 ? 'bg-red-500/15 border border-red-400/40' : 'bg-emerald-500/15 border border-emerald-400/40'}`}>
                <p className="text-xs uppercase tracking-wide font-semibold text-gray-200">{finance.shortfall > 0 ? 'Still Short' : 'Fully Funded'}</p>
                <p className={`text-3xl font-extrabold mt-0.5 ${finance.shortfall > 0 ? 'text-red-300' : 'text-emerald-300'}`} data-testid="finance-shortfall">
                  ${finance.shortfall.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard title="Total Attendees" value={stats.totalAttendees} icon={Users} color="bg-blue-500" />
        <StatCard title="Paid Full" value={stats.paidFull} icon={CheckCircle} color="bg-emerald-500" />
        <StatCard title="Deposit" value={stats.depositPaid} icon={DollarSign} color="bg-amber-500" />
        <StatCard title="Pending" value={stats.pending} icon={AlertCircle} color="bg-orange-500" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard title="Waitlist" value={stats.waitlist} icon={Users} color="bg-violet-500" />
        <StatCard title="Rooms" value={stats.totalRooms} icon={Home} color="bg-indigo-500" sub={`${stats.unassignedAttendees} unassigned`} />
        <StatCard
          title="Scholarships"
          value={stats.scholarshipCount}
          icon={Heart}
          color="bg-pink-500"
          sub={stats.scholarshipAmountTotal > 0 ? `$${stats.scholarshipAmountTotal.toLocaleString()} awarded` : 'amounts TBD'}
        />
      </div>

      {/* Settlement balance cards — Refunds owed (debt) + Donations received (gift). Each
          renders only when active so the row doesn't take space unnecessarily. */}
      {(stats.refundsOwedCount > 0 || stats.donorCount > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {stats.refundsOwedCount > 0 && (
            <Link to="/admin/retreat/refunds" className="block" data-testid="dashboard-refunds-card-link">
              <Card className="border-0 shadow-sm bg-gradient-to-br from-red-500 to-rose-500 text-white hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold tracking-widest uppercase text-white/80">Refunds owed</p>
                    <p className="text-2xl font-bold mt-1">${stats.totalRefundsOwed.toLocaleString()}</p>
                    <p className="text-xs text-white/80 mt-0.5">
                      to {stats.refundsOwedCount} {stats.refundsOwedCount === 1 ? 'person' : 'people'} — click to settle
                    </p>
                  </div>
                  <div className="text-white/70 text-xl">→</div>
                </CardContent>
              </Card>
            </Link>
          )}
          {stats.donorCount > 0 && (
            <Link to="/admin/retreat/donations" className="block" data-testid="dashboard-donations-card-link">
              <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-500 to-orange-500 text-white hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <HeartHandshake className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold tracking-widest uppercase text-white/80">Donations from cancellations</p>
                    <p className="text-2xl font-bold mt-1">${stats.totalDonated.toLocaleString()}</p>
                    <p className="text-xs text-white/80 mt-0.5">from {stats.donorCount} {stats.donorCount === 1 ? 'donor' : 'donors'} — click to manage</p>
                  </div>
                  <div className="text-white/70 text-xl">→</div>
                </CardContent>
              </Card>
            </Link>
          )}
        </div>
      )}

      {/* Recent cancellations card — visible only when at least one cancellation in last 7 days */}
      {stats.recentCancellationCount > 0 && (
        <Card className="border-0 shadow-sm mb-6 border-l-4 border-l-red-500" data-testid="recent-cancellations-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                Recent cancellations
                <span className="text-xs font-normal text-gray-500">(last 7 days)</span>
              </span>
              <Link to="/admin/retreat/attendees" className="text-xs font-medium text-red-600 hover:text-red-700 underline-offset-2 hover:underline">
                View all ({stats.cancelledCount}) →
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.recentCancellations.slice(0, 5).map(c => {
                const when = new Date(c.cancelled_at);
                const days = Math.floor((Date.now() - when.getTime()) / 86400000);
                const ago = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
                return (
                  <div
                    key={c.id}
                    className="flex items-center justify-between px-3 py-2 bg-red-50/60 border border-red-100 rounded-lg"
                    data-testid={`recent-cancellation-${c.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {c.first_name} {c.last_name}
                        <span className="text-xs font-normal text-gray-500 ml-2">{ago}</span>
                      </p>
                      {c.cancellation_reason && (
                        <p className="text-xs text-gray-600 truncate mt-0.5 italic">"{c.cancellation_reason}"</p>
                      )}
                    </div>
                    <span className="text-xs font-semibold text-red-700 whitespace-nowrap ml-2">
                      {c.cancelled_by === 'attendee' ? 'self' : 'admin'}
                    </span>
                  </div>
                );
              })}
              {stats.recentCancellations.length > 5 && (
                <p className="text-xs text-gray-500 text-center pt-1">
                  +{stats.recentCancellations.length - 5} more in the last 7 days
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scholarship roster — visible only when at least one recipient exists */}
      {stats.scholarshipCount > 0 && (
        <Card className="border-0 shadow-sm mb-6" data-testid="scholarship-roster">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Heart className="w-4 h-4 text-pink-500" />
              Scholarship recipients ({stats.scholarshipCount})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {stats.scholarshipRecipients
                .slice()
                .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''))
                .map(a => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between px-3 py-2 bg-pink-50/60 border border-pink-100 rounded-lg"
                    data-testid={`scholarship-row-${a.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {a.first_name} {a.last_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{a.email}</p>
                    </div>
                    <span className="text-xs font-semibold text-pink-700 whitespace-nowrap ml-2">
                      {a.scholarship_amount > 0 ? `$${a.scholarship_amount.toLocaleString()}` : 'TBD'}
                    </span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Link to="/admin/retreat/attendees" className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center group">
              <Users className="w-7 h-7 mx-auto mb-2 text-blue-600 group-hover:scale-110 transition-transform" />
              <p className="font-semibold text-sm text-gray-800">Manage Attendees</p>
            </Link>
            <Link to="/admin/retreat/rooms" className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center group">
              <Home className="w-7 h-7 mx-auto mb-2 text-emerald-600 group-hover:scale-110 transition-transform" />
              <p className="font-semibold text-sm text-gray-800">Assign Rooms</p>
            </Link>
            <Link to="/admin/retreat/check-in" className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center group">
              <ScanLine className="w-7 h-7 mx-auto mb-2 text-orange-600 group-hover:scale-110 transition-transform" />
              <p className="font-semibold text-sm text-gray-800">Check-In Scanner</p>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

function StatCard({ title, value, icon: Icon, color, sub }) {
  return (
    <Card className="border-0 shadow-sm overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-500 font-medium">{title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
          </div>
          <div className={`w-9 h-9 rounded-lg ${color} bg-opacity-10 flex items-center justify-center`}>
            <Icon className={`w-4 h-4 ${color.replace('bg-', 'text-')}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default RetreatDashboard;
